import { useState } from "react";
import { useTranslation } from "react-i18next";

import { NoticeToast } from "../../components/game/Notices.js";
import { TurnToast } from "../../components/game/TurnToast.js";
import { sideLabel } from "../../lib/roster.js";
import { cn } from "../../lib/utils.js";
import { refusalText } from "../../lib/refusal.js";
import {
  advance,
  allowSteal,
  deal,
  defendSteal,
  lockGuess,
  passChallenge,
  purchase,
  type GameLink,
  type GameView,
} from "../../services/gameLink.js";
import { roundCue } from "./cue.js";
import { RoundExtras } from "./RoundExtras.js";
import { PrimaryAction } from "./PrimaryAction.js";
import { RoundStage } from "./RoundStage.js";
import { Sides } from "./Sides.js";
import { roundStep, sidesHiddenFromRoster } from "./step.js";

/**
 * The phone, mid-game — a **stepper** since phase 18.
 *
 * # What this was, and why it is not that any more
 *
 * One scrolling column, in this order: header, the whole board, the guess
 * panels, the rating card, the primary action, the shop, the notice feed, the
 * exit. Finding #12 measured the consequence: **both ends drift away** while the
 * middle — the board — owns the screen. What a player needs constantly was at
 * the two ends and what they need occasionally was in between, which is exactly
 * inverted; and at four players on a narrow phone the board *is* the fold, so
 * the clock (#3) and the primary action were both off screen while somebody was
 * playing.
 *
 * The room asked for a stepper (#23), and it answers six findings by
 * construction rather than one at a time — #3, #10, #12, #16, #17, #20. Building
 * a sticky header first and then replacing it with a stepper would have been the
 * same work twice, which is why the frame was settled before any of it was
 * built.
 *
 * # Three bands, and only the middle one moves — 20d
 *
 * | | |
 * |---|---|
 * | **top** | round · what to do · the clock · coins |
 * | the **stage** | one question, from [`roundStep`](./step.js) |
 * | **bottom** | the one primary action, or what the room is waiting for |
 *
 * Phase 18 built the top and the bottom as `sticky`/`fixed` over a page-level
 * scroll. Finding 3.13 is what that leaves: the chrome stays put, but the *page*
 * still scrolls, so the stage's own content goes off screen and a control you
 * have to scroll to is a lost round. On a phone the panel is now exactly the
 * viewport tall and the middle band is the only scroll container in it — the
 * chrome cannot be scrolled away because it is not in a scroll container at all.
 *
 * Everything that is merely *available* — the board, the shop, the feed, the
 * exit — is in [`RoundExtras`](./RoundExtras.js): a drawer on a phone, and a
 * second column on a desktop, where finding #20 measured a thousand empty pixels
 * either side of a 768px column that scrolled anyway.
 *
 * ⚠️ **One action, not a bar of them.** Phase 14 B2 settled that answering is
 * primary and locking is secondary — *"two buttons of equal weight is how a
 * player ends up locking an empty guess"* — and a sticky bar is exactly where
 * that mistake gets re-made. So the bar holds one control, and a stage whose
 * action is *positional* (choosing a gap) gets the instruction instead.
 *
 * ⚠️ **Gated on the viewport, never on `deviceLayout`.** Couch mode is a phone
 * beside a television and remote mode may well be a laptop; what decides whether
 * chrome should stick is how much screen there is.
 *
 * # Nothing here decides anything
 *
 * Every control sends an intent and re-renders whatever comes back. The disabled
 * states are UX only — the engine refuses the same things again, and
 * `available_purchases` is its answer rather than a rule mirrored here.
 */
export function RoundPanel({ link, view }: { link: GameLink; view: GameView }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // **A picker belongs to the turn that opened it** — finding #19. This was a
  // bare `"challenge" | "steal" | null` written only by user action, so a player
  // who reached for steal and never picked a target kept the picker open when
  // the turn moved on — and worse, `StealPanel` unmounted with the state still
  // set, so the picker **re-opened by itself** the next time steal became
  // available. Carrying the round in the state says that in one place, with
  // nothing to keep in sync.
  const [targeting, setTargeting] = useState<{
    round: number;
    item: "challenge" | "steal";
  } | null>(null);

  const board = view.board;
  const you = view.you;
  if (!board) {
    return <p className="p-6 text-sm text-white/50">{t("round.waitingBoard")}</p>;
  }

  const aiming = targeting?.round === board.round_no ? targeting.item : null;
  // **Classic keeps its turn** (phase 14 C1). In Deathmatch and Team every side
  // plays every round, so "is it mine" is a question only Classic asks.
  const myTurn = !!you && (board.mode !== "classic" || you.side === board.active_side);
  const mayAdvance = you?.may_advance ?? false;
  const step = roundStep({ board, you: you ?? null, myTurn, mayAdvance });
  const cue = roundCue(
    { board, you: you ?? null, roster: view.roster, myTurn, mayAdvance },
    t,
  );

  /** Send an intent, and put the engine's refusal on screen if it refuses. */
  const send = (action: () => Promise<unknown>) => {
    setBusy(true);
    setRefusal(null);
    void action()
      .catch((cause: unknown) => {
        // What arrives is a *code*, so the sentence is this language's rather
        // than the server's — see `lib/refusal.ts`.
        setRefusal(refusalText(cause, t));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /**
   * Send an intent and let the caller see the refusal too.
   *
   * `send` deliberately swallows the rejection — nothing it drives has anything
   * to undo. An **optimistic** control does: the grids paint the tap before the
   * server has agreed (finding #2), so a refusal has to reach them or they keep
   * showing a choice that was declined.
   */
  const sendTracked = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      await action();
    } catch (cause: unknown) {
      setRefusal(refusalText(cause, t));
      throw cause;
    } finally {
      setBusy(false);
    }
  };

  const extras = <RoundExtras view={view} link={link} />;

  return (
    // 🔴 **Three bands, and only the middle one scrolls** — finding 3.13, built
    // in 20d. The chrome was `sticky`/`fixed` over a page-level scroll, which
    // keeps it on screen but leaves the *page* scrollable underneath: a phone
    // that had scrolled put the stage's own content out of reach, and reaching a
    // control by scrolling is a lost round in a fast-decision game.
    //
    // Now the header and the action bar are simply **not in a scroll container**
    // — the panel is exactly the viewport tall, and the stage between them takes
    // what is left. A stage that genuinely does not fit (a long timeline on a
    // small phone) scrolls *within its own band*, so the thing that gives is the
    // board and never the controls.
    //
    // ⚠️ Mobile only. From `lg` up the two-column layout is taller than the
    // viewport by design (finding #20) and the page scrolls normally, so every
    // rule here is undone at that breakpoint.
    //
    // 📌 Known and accepted: `PartyPage`'s reconnect banner is a sibling in
    // flow, so while the socket is down the page can scroll by the height of
    // that one line. A transient 36px beats an overlay that hides the header.
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden lg:h-auto lg:min-h-full lg:overflow-visible">
      {/* 🔴 **Centred, and the clock is not here any more** — 20d's second
          pass, on the maintainer's call against the rendered screens.

          The header is down to the two things that are true of *this moment*:
          which round and phase it is, and what to do about it. Centred, because
          with the dial gone there is nothing to balance a left-aligned sentence
          against and it read as hanging off the edge of the screen.

          ⚠️ **The purse is not here either.** It was pinned to this corner for
          one pass and it *floated* — absolutely positioned over a header whose
          instruction wraps to two lines on a 360px phone, so the chip sat on
          top of the sentence. Overlapping the one line that must never be
          truncated (3.1) is worse than the imbalance it was solving. It is at
          the top of the stage now, on the same row as the phase heading, which
          is a real row that can push things around instead of covering them.

          Left-aligned again from `lg`, where centring a single line across
          1440px would strand it.

          📌 The clock moved into the stage (`RoundStage`'s `StageBody`), which
          is what finding #3 now rests on — see the ⚠️ there. */}
      <header
        data-testid="round-chrome"
        className="relative z-20 flex shrink-0 flex-col items-center gap-1 border-b border-white/10 bg-stage-950/95 px-4 py-3 text-center backdrop-blur lg:sticky lg:top-0 lg:flex-row lg:items-start lg:justify-between lg:gap-3 lg:text-left"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
      >
        <div className="min-w-0 lg:flex-1">
          <p className="text-xs uppercase tracking-[0.2em] text-white/40">
            {t("round.number", { no: board.round_no })} · {cue.phase}
          </p>
          {/* **The one sentence on the screen** (phase 14 B2), phrased as what to
              do rather than what phase the engine is in. */}
          {/* ⚠️ **Never truncated** — finding 3.1. Every screenshot from the
              fourth party cut this line: *"Pon el siguiente clip cuando todo …"*,
              *"Responde en cuanto l…"*, *"Tranquilo — cualquier…"*. It is not a
              label, it is the instruction, and in `Dispute` it is the only thing
              on screen explaining what the countdown is for. It wraps instead,
              and the header grows to fit it. */}
          <h1
            aria-live="polite"
            className={cn(
              "text-balance text-base font-semibold leading-snug",
              cue.yours ? "text-white" : "text-white/60",
            )}
          >
            {cue.instruction}
          </h1>
        </div>
      </header>

      {refusal && (
        <p
          role="alert"
          className="mx-4 mt-3 shrink-0 rounded-lg bg-wrong-500/15 px-3 py-2 text-sm"
        >
          {refusal}
        </p>
      )}

      {/* The middle band: the only thing on this screen that scrolls. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* ⚠️ **A toast has to overlay — finding #17.** `NoticeToast` was a
            plain block in the document flow, in the middle of the long column
            this screen used to be, and it cleared itself after four seconds. So
            the victim of a steal *was* told — for four seconds, somewhere they
            were not looking.

            📌 **Finding 4.5, and why it is here rather than at a fixed offset.**
            It used to be `fixed` at `safe-area + 4.5rem` — a constant measured
            against a header that 20c then let *grow*, so a wrapped instruction
            (3.1) could put the header under its own toast. Anchored to the top
            of the stage band it is correct at every header height, and it
            floats clear of the action bar for free because the band ends above
            it. */}
        <div
          data-testid="notice-overlay"
          className="pointer-events-none absolute inset-x-0 top-2 z-30 flex flex-col items-center gap-2 px-4"
        >
          {/* Findings 3.6 and 3.6b — one finding in two directions. The room
              was left to work out whose turn it was from a header sentence that
              had been there since the previous round. ⚠️ Above the notice
              stack, because it is the frame the notices happen inside: *whose
              turn* explains *what just happened to whom*. */}
          <TurnToast
            roundNo={board.round_no}
            yours={myTurn}
            who={sideLabel(view.roster, board.active_side)?.name ?? null}
          />
          <NoticeToast board={board} roster={view.roster} yourSide={you?.side ?? null} />
        </div>

        {/* Two columns from `lg` up — finding #20. The column was capped at 768px
            on a ~1780px window, so a thousand pixels sat empty either side while
            the same content scrolled as though it were on a phone. The cap has a
            good reason (`Sides.tsx`: a phone-shaped column is right when the board
            is elsewhere) and the reasoning that widened it once applies twice. */}
        <div
          data-testid="round-scroll"
          className="mx-auto flex w-full min-h-0 max-w-6xl flex-1 gap-6 overflow-y-auto px-4 pt-4 lg:overflow-visible lg:pb-6"
        >
          <div className="min-w-0 flex-1">
            <RoundStage
              step={step}
              view={view}
              link={link}
              busy={busy}
              aiming={aiming}
              onAim={(item) => {
                setTargeting(item === null ? null : { round: board.round_no, item });
              }}
              send={send}
              sendTracked={sendTracked}
            />

            {/* 🔴 **The rest of the room goes under the question, not beside
                it.** The roster spent one pass inside the powers column, which
                is 320px wide — six cards folded into a one-card ribbon and the
                board became unreadable, on the surface that has the most space
                of any. It is here now, under the stage, where a desktop has
                hundreds of empty pixels and `sm:grid-cols-2` has room to mean
                something.

                ⚠️ **The rest of the room, and not you.** Which sides are hidden
                is [`sidesHiddenFromRoster`](./step.js)'s answer, not a guess —
                and on your own turn it hides yours: *"yours shouldn't be there
                because you are playing, you are the active player."* */}
            <Sides
              board={board}
              rules={view.rules}
              roster={view.roster}
              yourSide={you?.side ?? null}
              layout={view.deviceLayout}
              omit={sidesHiddenFromRoster(step, board, you?.side ?? null)}
              className="mt-5"
            />

            {/* The phone's drawer for the same content. Below the stage rather
                than over it, so opening it never hides the question — and
                inside the scrolling band, because it is the one thing here that
                is *allowed* to be longer than the screen. */}
            {drawerOpen && (
              <div data-testid="round-drawer" className="pt-4 lg:hidden">
                {extras}
              </div>
            )}
          </div>
          <aside className="hidden w-80 shrink-0 lg:block">{extras}</aside>
        </div>
      </div>

      {/* 🔴 **Skip floats, and it is the one power that does** — ADR 0026 and
          finding 3.14. Everything else appears in the stage at the moment its
          window opens; skip is *always true of your turn* — `Listening`,
          `Guessing` and `Placing`, active side only — so it holds a fixed
          position instead of arriving and leaving among things that come and go.

          *"Reaching skip costs a tap on Más plus a scroll."* It sits above the
          action bar, on the right, clear of the primary control: the two are
          never the same decision, and a player reaching for one must not find
          the other under their thumb.

          ⚠️ The condition is the engine's own list, not a phase check here. */}
      <div className="relative shrink-0">
        {(you?.available_purchases ?? []).includes("skip") && (
          <div className="pointer-events-none absolute inset-x-0 bottom-full z-20 flex justify-end px-4 pb-3">
            <button
              type="button"
              data-testid="skip-power"
              disabled={busy || (you?.coins ?? 0) < view.rules.skip}
              onClick={() => {
                send(() => purchase(link, "skip"));
              }}
              className="pointer-events-auto flex min-h-12 items-center gap-2 rounded-full border border-coin-400/40 bg-stage-950/95 px-5 text-sm font-semibold shadow-lg backdrop-blur transition-colors hover:border-coin-400/70 disabled:opacity-40"
            >
              {t("shop.items.skip.name")}
              <span className="tabular-nums text-coin-400">{view.rules.skip}</span>
            </button>
          </div>
        )}

        <nav
          data-testid="round-action"
          className="z-20 flex shrink-0 items-center gap-3 border-t border-white/10 bg-stage-950/95 px-4 py-3 backdrop-blur lg:hidden"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)",
          }}
        >
          <PrimaryAction
            step={step}
            busy={busy}
            canLock={
              (you?.artist_option ?? null) !== null ||
              (you?.title_option ?? null) !== null
            }
            onDeal={() => {
              send(() => (board.phase === "settled" ? advance(link) : deal(link)));
            }}
            onLock={() => {
              send(() => lockGuess(link));
            }}
            onPass={() => {
              send(() => passChallenge(link));
            }}
            onDefend={() => {
              send(() => defendSteal(link));
            }}
            onAllow={() => {
              send(() => allowSteal(link));
            }}
            canDefend={(you?.coins ?? 0) >= (board.pending_steal?.defence_price ?? 0)}
            onRematch={() => {
              send(() => link.backToLobby());
            }}
            gameId={view.gameId}
          />
          {/* 🔴 **A cog, not a word** — ADR 0026's third consequence. *"A labelled
            Más next to the primary action invites a player to go looking in it
            again, which is the behaviour being removed."*

            Six findings were one report — *"this is buried in Más"* — and the
            container is what is being dismantled, not its contents: what was in
            it now lives where it is used. What is left behind the cog is the
            scrollback and the way out, which is the ADR's *rare and
            administrative* row.

            ⚠️ It keeps an accessible name and a 48px target. An icon-only
            control a screen reader cannot name is a worse drawer than a
            labelled one. */}
          <button
            type="button"
            data-testid="round-cog"
            aria-expanded={drawerOpen}
            aria-label={t("round.extras.label")}
            onClick={() => {
              setDrawerOpen((open) => !open);
            }}
            className="flex min-h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-white/20 text-white/70 transition-colors hover:border-white/40 hover:text-white"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="12" r="3.2" />
              <path d="M19.9 14.6a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.55v.09a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1.03H3.4a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.08a1.7 1.7 0 0 0 1.03-1.55V3.4a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.08a1.7 1.7 0 0 0 1.55 1.03h.09a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1.03z" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );
}
